import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import tw from 'twin.macro';
import { Link } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { Modal } from '../modal';
import { RESEND_CONFIRMATION_CODE, SIGN_UP } from '../../services/authService/queries';
import { useMutation, useQuery } from '@apollo/client';
import { POLICY_DOCUMENTS } from '../../services/policyService/queries';
import {
  signUpPolicyIds,
  type PolicyDocumentRow,
} from '../../services/policyService/checkoutPolicies';
import { toast } from 'react-toastify';
import {
  IResendConfirmationCodeMutation,
  IResendConfirmationCodeMutationVariables,
  ISignUpMutation,
  ISignUpMutationVariables,
} from '../../services/authService/__generated__/queries.generated';
import { modalActions } from '../../../store/authStore/modalStore';
import {
  isConfirmedExistingAccountResendError,
  isConfirmedExistingAccountSignUpError,
  shouldRecoverUnverifiedSignUp,
} from '../../helpers/authErrorHelpers';
import { Colors } from '../../styles/colors';
import {
  AUTH_FORM_COPY,
  getPasswordRuleChecks,
  validateSignUpForm,
} from '../../utils/authFormValidation';
import {
  DividerContainer,
  DividerLine,
  DividerText,
  GoogleSignInButton,
} from './GoogleSignInButton';
import { showGoogleSignIn } from '../../../config/runtime-target';

const Container = styled.div`
  ${tw`w-full max-w-md mx-auto text-left`}
`;

const Title = styled.h2`
  ${tw`text-2xl md:text-3xl font-bold mb-6`}
`;

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

const NameRow = styled.div`
  ${tw`grid grid-cols-1 sm:grid-cols-2 gap-4`}
`;

const NameField = styled.div`
  ${tw`min-w-0`}
`;

const Label = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-2`}
`;

const Input = styled.input<{ $invalid?: boolean }>`
  ${tw`
    w-full
    box-border
    h-11
    px-3
    border border-solid border-border-default
    rounded-md
    text-base text-text-primary
    placeholder-gray-400
  `}
  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: ${Colors.Error};
    `}
`;

const FieldError = styled.p`
  ${tw`text-sm mt-1 mb-0`}
  color: ${Colors.Error};
`;

const HelpText = styled.p`
  ${tw`text-xs text-text-secondary mt-2 mb-4`}
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

const CheckboxContainer = styled.div`
  ${tw`flex items-start gap-2 mb-4`}
`;

const TermsRow = styled.div<{ $shake?: boolean }>`
  ${tw`flex items-start gap-2`}
  ${({ $shake }) =>
    $shake &&
    css`
      animation: ${shakeAnimation} 0.5s ease-in-out;
    `}
`;

const Checkbox = styled.input<{ $error?: boolean }>`
  ${tw`mt-1 shrink-0`}
  ${({ $error }) =>
    $error &&
    css`
      accent-color: ${Colors.Error};
      outline: 2px solid ${Colors.Error};
      outline-offset: 1px;
    `}
`;

const CheckboxLabel = styled.label<{ $error?: boolean }>`
  ${tw`text-sm leading-snug`}
  color: ${({ $error }) => ($error ? Colors.Error : 'var(--color-text-secondary)')};
`;

const TermsError = styled.p`
  ${tw`text-sm mt-2 mb-0`}
  color: ${Colors.Error};
`;

const Button = styled.button<{ disabled?: boolean }>`
  ${tw`w-full bg-black text-white py-2.5 rounded-md font-medium inline-flex items-center justify-center gap-2 max-w-full min-w-0 box-border`}
  ${props => props.disabled && tw`opacity-50 cursor-not-allowed`}

  &:hover:not(:disabled) {
    ${tw`bg-indigo-700`}
  }
`;

const Footer = styled.div`
  ${tw`mt-6 text-center text-sm text-text-secondary`}
`;

const TextLink = styled.button`
  ${tw`text-black cursor-pointer border-none bg-transparent p-0 inline text-inherit`}

  &:hover {
    ${tw`text-black underline`}
  }
`;

const PolicyLink = styled(Link)<{ $error?: boolean }>`
  ${tw`underline`}
  color: ${({ $error }) => ($error ? Colors.Error : 'var(--color-black)')};

  &:hover {
    ${tw`underline`}
  }
`;

const SocialTerms = styled.p`
  ${tw`-mt-2 mb-0 text-xs text-center text-text-secondary`}
`;

const PasswordRequirements = styled.div`
  ${tw`mt-2 space-y-1`}
`;

const RequirementRow = styled.div`
  ${tw`flex items-center`}
`;

const RequirementIcon = styled.span<{ isValid: boolean }>`
  ${tw`w-5 text-center text-sm shrink-0`}
  color: ${props => (props.isValid ? '#34C759' : 'var(--color-text-tertiary)')};
`;

const RequirementText = styled.span<{ isValid: boolean }>`
  ${tw`text-xs ml-2`}
  color: ${props => (props.isValid ? 'var(--color-text-secondary)' : 'var(--color-text-tertiary)')};
`;

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignUpModal = ({ isOpen }: SignUpModalProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [emailMarketingOptIn, setEmailMarketingOptIn] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [termsShakeKey, setTermsShakeKey] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
  }>({});

  const passwordValidation = getPasswordRuleChecks(password);

  const [resendConfirmationCode] = useMutation<
    IResendConfirmationCodeMutation,
    IResendConfirmationCodeMutationVariables
  >(RESEND_CONFIRMATION_CODE);

  const openVerifyForExistingUnconfirmedAccount = async () => {
    const trimmedEmail = email.trim();
    modalActions.setCurrentEmail(trimmedEmail);

    try {
      await resendConfirmationCode({ variables: { email: trimmedEmail } });
      toast.success('This email is already pending verification. We sent a new verification code.');
      modalActions.closeSignUpModal();
      modalActions.openVerifyCodeModal(trimmedEmail);
    } catch (resendError: any) {
      const message = resendError?.message || '';

      if (isConfirmedExistingAccountResendError(message)) {
        toast.error(
          'Email already exists. Please sign in with Google or use Forgot password to set a new password.',
          { autoClose: 8000 },
        );
        modalActions.switchToSignIn(trimmedEmail);
        return;
      }

      // Signup duplicate was not a confirmed-password account — still guide to verify
      // (resend may fail on rate limit etc.; user may already have a code).
      toast.info(
        'This email is already pending verification. Please enter your verification code.',
      );
      modalActions.closeSignUpModal();
      modalActions.openVerifyCodeModal(trimmedEmail);
    }
  };

  const policyDocuments = useQuery(POLICY_DOCUMENTS, { errorPolicy: 'all' });
  const [signUp, { loading, error }] = useMutation<ISignUpMutation, ISignUpMutationVariables>(
    SIGN_UP,
    {
      onCompleted: data => {
        toast.success('Please check your email for a confirmation link');
        modalActions.closeSignUpModal();
        modalActions.openVerifyCodeModal(email);
      },
      onError: error => {
        const msg = error.message || '';

        if (isConfirmedExistingAccountSignUpError(msg)) {
          toast.error(
            'Email already exists. Please sign in or use Forgot password if you already registered.',
            { autoClose: 8000 },
          );
          modalActions.switchToSignIn(email.trim());
          return;
        }

        if (shouldRecoverUnverifiedSignUp(msg)) {
          openVerifyForExistingUnconfirmedAccount();
          return;
        }

        toast.error(error.message);
      },
    },
  );

  const handleSignUp = () => {
    const validation = validateSignUpForm({
      firstName,
      lastName,
      email,
      password,
      agreedToTerms,
    });
    setFieldErrors(validation.errors);
    setShowTermsError(validation.termsInvalid);
    if (validation.termsInvalid) {
      setTermsShakeKey(key => key + 1);
    }
    if (!validation.ok) {
      if (validation.toast) {
        toast.error(validation.toast);
      }
      return;
    }

    const acceptedPolicyVersionIds = signUpPolicyIds(
      (policyDocuments.data?.policyDocuments ?? []) as PolicyDocumentRow[],
    );
    signUp({
      variables: {
        input: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          password,
          acceptedTerms: true,
          emailMarketingOptIn,
          ...(acceptedPolicyVersionIds.length ? { acceptedPolicyVersionIds } : {}),
        },
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalActions.closeSignUpModal}
      modalWidth="min(480px, calc(100vw - 2rem))"
    >
      <Container data-testid="signup-modal-container">
        <Title>Let's get started</Title>

        <FormGroup>
          <NameRow>
            <NameField>
              <Label htmlFor="signup-first-name">First name</Label>
              <Input
                id="signup-first-name"
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={e => {
                  setFirstName(e.target.value);
                  setFieldErrors(prev => ({ ...prev, firstName: undefined }));
                }}
                $invalid={!!fieldErrors.firstName}
                aria-invalid={!!fieldErrors.firstName}
                autoComplete="given-name"
                data-testid="signup-firstname-input"
              />
              {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
            </NameField>
            <NameField>
              <Label htmlFor="signup-last-name">Last name</Label>
              <Input
                id="signup-last-name"
                type="text"
                placeholder="Enter your last name"
                value={lastName}
                onChange={e => {
                  setLastName(e.target.value);
                  setFieldErrors(prev => ({ ...prev, lastName: undefined }));
                }}
                $invalid={!!fieldErrors.lastName}
                aria-invalid={!!fieldErrors.lastName}
                autoComplete="family-name"
                data-testid="signup-lastname-input"
              />
              {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
            </NameField>
          </NameRow>
          <HelpText>Enter your name as it appears on your driver&apos;s license</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setFieldErrors(prev => ({ ...prev, email: undefined }));
            }}
            autoComplete="email"
            data-testid="signup-email-input"
            $invalid={!!fieldErrors.email}
            aria-invalid={!!fieldErrors.email}
          />
          {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setFieldErrors(prev => ({ ...prev, password: undefined }));
            }}
            data-testid="signup-password-input"
            autoComplete="new-password"
            $invalid={!!fieldErrors.password}
            aria-invalid={!!fieldErrors.password}
          />
          {fieldErrors.password && <FieldError>{fieldErrors.password}</FieldError>}
          {(password.length > 0 || !!fieldErrors.password) && (
            <PasswordRequirements>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.minLength}>
                  {passwordValidation.minLength ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.minLength}>
                  At least 8 characters
                </RequirementText>
              </RequirementRow>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.hasUppercase}>
                  {passwordValidation.hasUppercase ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.hasUppercase}>
                  At least 1 uppercase letter
                </RequirementText>
              </RequirementRow>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.hasLowercase}>
                  {passwordValidation.hasLowercase ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.hasLowercase}>
                  At least 1 lowercase letter
                </RequirementText>
              </RequirementRow>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.hasNumber}>
                  {passwordValidation.hasNumber ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.hasNumber}>
                  At least 1 number
                </RequirementText>
              </RequirementRow>
            </PasswordRequirements>
          )}
        </FormGroup>

        <CheckboxContainer>
          <TermsRow key={termsShakeKey} $shake={showTermsError} data-testid="signup-terms-row">
            <Checkbox
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              $error={showTermsError}
              onChange={e => {
                setAgreedToTerms(e.target.checked);
                if (e.target.checked) {
                  setShowTermsError(false);
                }
              }}
              data-testid="signup-terms-agreement"
            />
            <CheckboxLabel htmlFor="terms" $error={showTermsError}>
              I agree to the{' '}
              <PolicyLink
                to="/policies/terms"
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                $error={showTermsError}
              >
                terms of service
              </PolicyLink>{' '}
              and{' '}
              <PolicyLink
                to="/policies/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                $error={showTermsError}
              >
                privacy policy
              </PolicyLink>
            </CheckboxLabel>
          </TermsRow>
        </CheckboxContainer>
        {showTermsError && <TermsError>{AUTH_FORM_COPY.termsInline}</TermsError>}

        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="promotions"
            checked={emailMarketingOptIn}
            onChange={e => setEmailMarketingOptIn(e.target.checked)}
            data-testid="signup-promotions-checkbox"
          />
          <CheckboxLabel htmlFor="promotions">
            Send me promotions and announcements via email
          </CheckboxLabel>
        </CheckboxContainer>

        <Button
          type="button"
          onClick={handleSignUp}
          disabled={loading}
          aria-busy={loading}
          data-testid="signup-submit-button"
        >
          Sign up
          {loading && <MoonLoader color="#ffffff" size={16} />}
        </Button>

        {showGoogleSignIn() && (
          <>
            <DividerContainer>
              <DividerLine />
              <DividerText>or</DividerText>
              <DividerLine />
            </DividerContainer>

            <GoogleSignInButton title="Sign up with Google" dataTestId="signup-google-button" />
            <SocialTerms>
              By continuing you agree to the{' '}
              <PolicyLink to="/policies/terms" target="_blank" rel="noopener noreferrer">
                Terms
              </PolicyLink>{' '}
              and{' '}
              <PolicyLink to="/policies/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </PolicyLink>
            </SocialTerms>
          </>
        )}

        <Footer>
          Already have an account?{' '}
          <TextLink
            type="button"
            onClick={() => modalActions.switchToSignIn()}
            data-testid="signup-signin-link"
          >
            Log in
          </TextLink>
        </Footer>

        <Footer>
          Have a verification code?{' '}
          <TextLink
            type="button"
            onClick={() => modalActions.switchToVerifyCode(email.trim() || undefined)}
          >
            Enter code
          </TextLink>
        </Footer>
      </Container>
    </Modal>
  );
};

export default SignUpModal;
